import React, { useEffect, useState } from "react";
import { Card, Badge, Table, Button, Modal, Form, Row, Col } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import "../common/Chips.css";

// Phase 3D, §31. Read-only-by-default Test Execution section for
// FormSubmissionViewPage.jsx — kept visually and conceptually separate from
// the existing Phase 3C Form Score/Result section (a UAT pass rate and a
// form score are genuinely different numbers). Every count/rate shown here
// is a straight passthrough of the backend's own stored/recalculated
// summary — nothing is computed in the browser. Task actions (create/link/
// retry/unlink) are attempted directly; a 403 from the backend (no
// `form_task_links:*` permission) is surfaced as a normal toast, not a
// pre-emptive UI guess about the caller's permissions.
function resultVariant(code) {
  switch (code) {
    case "PASS": return "success";
    case "FAIL": return "danger";
    case "BLOCKED": return "warning";
    case "NOT_APPLICABLE": return "secondary";
    default: return "light";
  }
}

export default function FormTestExecutionPanel({ submissionId }) {
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryErrorIsPermission, setSummaryErrorIsPermission] = useState(false);
  const [failedCases, setFailedCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const [taskTypes, setTaskTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectModules, setProjectModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [pickersLoaded, setPickersLoaded] = useState(false);

  const [createModal, setCreateModal] = useState(null); // the row being acted on
  const [createForm, setCreateForm] = useState({});
  const [linkModal, setLinkModal] = useState(null);
  const [linkTaskId, setLinkTaskId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/form-submissions/${submissionId}/test-summary`);
      setSummary(res.data?.data);
      setSummaryError(null);
      setSummaryErrorIsPermission(false);
    } catch (err) {
      setSummary(null);
      setSummaryError(err.response?.data?.code === "FORM_TEST_SUMMARY_NOT_AVAILABLE" ? null : err.response?.data?.message);
      // Lack of access to view this summary is an expected permission
      // state, not a failure -- surfaced with neutral/info styling below
      // instead of the red error treatment a real failure gets.
      setSummaryErrorIsPermission(err.response?.status === 403);
    }
    try {
      const res = await api.get(`/form-submissions/${submissionId}/failed-test-cases`);
      setFailedCases(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (_e) {
      setFailedCases([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const loadPickers = async () => {
    if (pickersLoaded) return;
    const [ttRes, pRes, uRes] = await Promise.all([
      api.get("/task-types", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/users").catch(() => null),
    ]);
    setTaskTypes(Array.isArray(ttRes?.data?.data) ? ttRes.data.data : []);
    setProjects(Array.isArray(pRes?.data?.data) ? pRes.data.data : []);
    setUsers(Array.isArray(uRes?.data) ? uRes.data : []);
    setPickersLoaded(true);
  };

  const fetchModulesForProject = async (projectId) => {
    if (!projectId) { setProjectModules([]); return; }
    try {
      const res = await api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } });
      setProjectModules(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (_e) {
      setProjectModules([]);
    }
  };

  const recalculate = async () => {
    setRecalculating(true);
    try {
      const res = await api.post(`/form-submissions/${submissionId}/test-summary/recalculate`, {});
      setSummary(res.data?.data);
      toast.success("Test summary recalculated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to recalculate summary");
    } finally {
      setRecalculating(false);
    }
  };

  const openCreate = async (row) => {
    await loadPickers();
    setCreateForm({ task_type_id: "", title: "", description: "", priority: "", project_id: "", project_module_id: "", assigned_to: "" });
    setCreateModal(row);
  };

  const submitCreate = async () => {
    if (!createForm.task_type_id) { toast.warning("Task type is required"); return; }
    setSaving(true);
    try {
      const payload = {
        task_type_id: Number(createForm.task_type_id),
        title: createForm.title || undefined,
        description: createForm.description || undefined,
        priority: createForm.priority || undefined,
        project_id: createForm.project_id || undefined,
        project_module_id: createForm.project_module_id || undefined,
        assigned_to: createForm.assigned_to || undefined,
      };
      await api.post(`/form-submissions/${submissionId}/repeating-rows/${createModal.id}/create-task`, payload);
      toast.success("Task created and linked");
      setCreateModal(null);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_TASK_ALREADY_LINKED") toast.error("This test case already has an active linked task.");
      else toast.error(data?.message || "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  const openLink = (row) => { setLinkModal(row); setLinkTaskId(""); };

  const submitLink = async () => {
    if (!linkTaskId) { toast.warning("Task ID is required"); return; }
    setSaving(true);
    try {
      await api.post(`/form-submissions/${submissionId}/repeating-rows/${linkModal.id}/link-task`, { task_id: Number(linkTaskId), link_type: "TEST_FAILURE" });
      toast.success("Task linked");
      setLinkModal(null);
      load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_TASK_ALREADY_LINKED") toast.error("This task is already linked to this test case.");
      else toast.error(data?.message || "Failed to link task");
    } finally {
      setSaving(false);
    }
  };

  const retry = async (row) => {
    try {
      await api.post(`/form-submissions/${submissionId}/repeating-rows/${row.id}/retry-task-creation`, {});
      toast.success("Retry completed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Retry failed");
    }
  };

  const unlink = async (row, link) => {
    if (!window.confirm("Unlink this task from this test case? The task itself will not be deleted.")) return;
    try {
      await api.delete(`/form-submissions/${submissionId}/repeating-rows/${row.id}/task-links/${link.id}`);
      toast.success("Task unlinked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to unlink task");
    }
  };

  if (loading) return null;
  if (!summary && !failedCases.length && !summaryError) return null;

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2 fsv-test-header">
          <strong>Test Execution</strong>
          <Button variant="outline-secondary" className="fsv-btn-secondary" disabled={recalculating} onClick={recalculate}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            {recalculating ? "Recalculating..." : "Recalculate"}
          </Button>
        </div>

        {summaryError && (
          <div className={`fsv-notice mb-2 ${summaryErrorIsPermission ? "fsv-notice-info" : "fsv-notice-danger"}`} role="status">
            <i className={`fa-solid ${summaryErrorIsPermission ? "fa-circle-info" : "fa-triangle-exclamation"}`} aria-hidden="true"></i>
            <span>{summaryError}</span>
          </div>
        )}

        {summary && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="chip chip-neutral">Total<strong>{summary.total_test_cases}</strong></span>
            <span className="chip chip-neutral">Executed<strong>{summary.executed_test_cases}</strong></span>
            <span className="chip chip-success">Passed<strong>{summary.passed_test_cases}</strong></span>
            <span className="chip chip-danger">Failed<strong>{summary.failed_test_cases}</strong></span>
            <span className="chip chip-warning">Blocked<strong>{summary.blocked_test_cases}</strong></span>
            <span className="chip chip-neutral">N/A<strong>{summary.not_applicable_test_cases}</strong></span>
            <span className="chip chip-neutral">Not Executed<strong>{summary.not_executed_test_cases}</strong></span>
            <span className="chip chip-blue">Pass Rate<strong>{summary.pass_rate != null ? `${summary.pass_rate}%` : "—"}</strong></span>
            <span className="chip chip-blue">Completion<strong>{summary.completion_rate != null ? `${summary.completion_rate}%` : "—"}</strong></span>
          </div>
        )}

        {failedCases.length > 0 && (
          <>
            <strong className="small d-block mb-2">Failed / Blocked Test Cases</strong>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>#</th><th>Test Case</th><th>Result</th><th>Severity</th><th>Expected</th><th>Actual</th><th>Evidence</th><th>Linked Task</th><th></th>
                </tr>
              </thead>
              <tbody>
                {failedCases.map((row) => {
                  const activeLink = (row.links || []).find((l) => l.link_status === "ACTIVE");
                  return (
                    <tr key={row.id}>
                      <td>{row.row_number}</td>
                      <td>{row.test_case_id || row.test_case_title || "—"}</td>
                      <td><Badge bg={resultVariant(row.result_status)} text={resultVariant(row.result_status) === "warning" ? "dark" : undefined}>{row.result_status}</Badge></td>
                      <td>{row.severity || "—"}</td>
                      <td className="small" style={{ maxWidth: 160 }}>{row.expected_result || "—"}</td>
                      <td className="small" style={{ maxWidth: 160 }}>{row.actual_result || "—"}</td>
                      <td>{row.evidence_count || 0}</td>
                      <td className="small">
                        {activeLink ? (
                          <>
                            {activeLink.task_title || `Task #${activeLink.task_id}`}
                            <Badge bg="light" text="dark" className="ms-1">{activeLink.task_status_legacy || "—"}</Badge>
                          </>
                        ) : "—"}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {!activeLink && <Button size="sm" variant="outline-primary" onClick={() => openCreate(row)}>Create Task</Button>}
                          {!activeLink && <Button size="sm" variant="outline-secondary" onClick={() => openLink(row)}>Link Existing</Button>}
                          {!activeLink && <Button size="sm" variant="outline-warning" onClick={() => retry(row)}>Retry Rule</Button>}
                          {activeLink && <Button size="sm" variant="outline-danger" onClick={() => unlink(row, activeLink)}>Unlink</Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>

      <Modal show={!!createModal} onHide={() => setCreateModal(null)} centered dialogClassName="fsv-modal">
        <Modal.Header closeButton><Modal.Title>Create Task for Test Case</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col md={12}>
              <Form.Label className="small">Task Type</Form.Label>
              <Form.Select size="sm" value={createForm.task_type_id || ""} onChange={(e) => setCreateForm({ ...createForm, task_type_id: e.target.value })}>
                <option value="">Select...</option>
                {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label className="small">Title (optional — a default is generated if left blank)</Form.Label>
              <Form.Control size="sm" value={createForm.title || ""} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} />
            </Col>
            <Col md={12}>
              <Form.Label className="small">Description (optional)</Form.Label>
              <Form.Control as="textarea" rows={2} size="sm" value={createForm.description || ""} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label className="small">Priority</Form.Label>
              <Form.Select size="sm" value={createForm.priority || ""} onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}>
                <option value="">(type default)</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Assignee (optional)</Form.Label>
              <Form.Select size="sm" value={createForm.assigned_to || ""} onChange={(e) => setCreateForm({ ...createForm, assigned_to: e.target.value })}>
                <option value="">None</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Project (optional override)</Form.Label>
              <Form.Select size="sm" value={createForm.project_id || ""} onChange={(e) => { setCreateForm({ ...createForm, project_id: e.target.value, project_module_id: "" }); fetchModulesForProject(e.target.value); }}>
                <option value="">(use assignment's project)</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Module (optional)</Form.Label>
              <Form.Select size="sm" value={createForm.project_module_id || ""} onChange={(e) => setCreateForm({ ...createForm, project_module_id: e.target.value })} disabled={!createForm.project_id}>
                <option value="">None</option>
                {projectModules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setCreateModal(null)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={submitCreate}>{saving ? "Creating..." : "Create Task"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!linkModal} onHide={() => setLinkModal(null)} centered dialogClassName="fsv-modal">
        <Modal.Header closeButton><Modal.Title>Link Existing Task</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Label className="small">Task ID</Form.Label>
          <Form.Control size="sm" type="number" value={linkTaskId} onChange={(e) => setLinkTaskId(e.target.value)} placeholder="e.g. 1042" />
          <div className="text-muted small mt-2">You must already have view access to this task. Linking is blocked if it's already linked to this test case.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setLinkModal(null)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={submitLink}>{saving ? "Linking..." : "Link Task"}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
